import {
  type BarData,
  FormatType,
  GroupedBarChart,
  type GroupedBarData,
  GroupedBarTooltip,
  getFormatter,
} from '@revelio/replots';

import { usePlotMetric } from '../../../hooks/usePlotMetric';
import type { BreakdownSingleDataSet } from '../../DataProvider/types';
import { useChart } from '../ChartProvider';
import { SharableChart } from '../chart-menu/plot-share/SharableChart';
import { getFormat } from '../utils/getFormat';
import { useActiveColors } from './useActiveColors';

export const GroupedBar = ({
  data,
  hideLegend,
  ignoreCustomTooltip,
}: {
  data: BreakdownSingleDataSet['data'];
  hideLegend?: boolean;
  ignoreCustomTooltip?: boolean;
}) => {
  const { item } = useChart();
  const metric = usePlotMetric();

  const { colorMap } = useActiveColors();

  const format = item?.plot?.format ?? getFormat({ metric });
  const showBarLabels = item?.plot?.showDataLabels ?? false;
  const orientation = item?.plot?.orientation ?? 'horizontal';

  const groupedBarData: GroupedBarData<{ count: number }>[] = data?.map(
    (item) => ({
      group: item.dim,
      values: item.breakdown.map((breakdown) => ({
        label: breakdown.dim,
        value: breakdown.share ?? 0,
        count: breakdown.value ?? 0,
        id: breakdown.id ?? null,
      })),
    })
  );

  const shortNameColorMap = (() => {
    const base: Record<string, string> = { ...(colorMap ?? {}) };
    const breakdownRows = data?.flatMap((d) => d.breakdown) ?? [];

    for (const breakdown of breakdownRows) {
      if (breakdown.id == null) continue;
      const idStr = String(breakdown.id);
      const shortKey = `${idStr}_${breakdown.dim}`;
      if (breakdown.longName) {
        const longKey = `${idStr}_${breakdown.longName}`;
        if (!base[shortKey] && base[longKey]) {
          base[shortKey] = base[longKey];
        }
      }
    }

    for (const breakdown of breakdownRows) {
      if (breakdown.id == null) continue;
      const idStr = String(breakdown.id);
      const shortKey = `${idStr}_${breakdown.dim}`;
      if (base[shortKey]) continue;
      const prefix = `${idStr}_`;
      const match = Object.entries(base).find(([k]) => k.startsWith(prefix));
      if (match) {
        base[shortKey] = match[1];
      }
    }

    return base;
  })();

  const tooltipContent = (group: string, bar: BarData & { count: number }) => {
    const tooltipValue = (() => {
      if (format === FormatType.PERCENTAGE) {
        return getFormatter(format)(bar?.value ?? 0);
      }

      const formattedCount = getFormatter(FormatType.INTEGER)(bar?.count || 0);
      const formattedValue = getFormatter(FormatType.PERCENTAGE)(
        (bar?.value ?? 0) / 100
      );
      return bar?.count !== undefined
        ? `${formattedCount} / ${formattedValue}`
        : formattedValue;
    })();

    return (
      <GroupedBarTooltip title={bar.label} label={group} value={tooltipValue} />
    );
  };

  return (
    <SharableChart
      Component={GroupedBarChart<{ count: number }>}
      data={groupedBarData}
      format={format}
      colorMap={shortNameColorMap}
      colorPalette={item?.plot?.colorPalette}
      {...(!ignoreCustomTooltip && { tooltipContent })}
      hideLegend={hideLegend}
      showBarLabels={showBarLabels}
      orientation={orientation}
    />
  );
};
